<script setup lang="ts">
/**
 * App Sidebar Component
 *
 * Main navigation sidebar
 */
import { RouterLink, useRoute } from 'vue-router';
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue';
import { X } from 'lucide-vue-next';
import { useUIStore, usePermissionsStore } from '@/stores';
import { Icon } from '@tms-phoenix/ui-vue';
import type { ModulePermissions } from '@/core/services/permissions.service';

const route = useRoute();
const uiStore = useUIStore();
const permissionsStore = usePermissionsStore();

function canAccessModule(moduleKey: keyof ModulePermissions): boolean {
  // Keep nav visible during initial permissions load to avoid an empty sidebar flash.
  if (!permissionsStore.isLoaded) return true;
  return permissionsStore.isModuleAllowed(moduleKey);
}

const navigation = computed(() => [
  { name: 'Product Management', href: '/products', icon: 'MilkInventory', moduleKey: 'productManagement', show: canAccessModule('productManagement') },
  { name: 'Tenant Management', href: '/onboarding', icon: 'Handshake', moduleKey: 'tenantManagement', show: canAccessModule('tenantManagement') },
  { name: 'User Management', href: '/users', icon: 'Users', moduleKey: 'userManagement', show: canAccessModule('userManagement') },
  { name: 'Recall Management', href: '/recall', icon: 'RecallManagement', moduleKey: 'recall', show: canAccessModule('recall') },
  { name: 'Enterprises', href: '/enterprises', icon: 'Hospital', iconVariant: 'stroke' as const, moduleKey: 'enterprisesManagement', show: canAccessModule('enterprisesManagement') },
  { name: 'Audit Logs', href: '/audit-logs', icon: 'CreateOrder', moduleKey: 'auditLogs', show: canAccessModule('auditLogs') },
].filter((item) => item.show));

const showSettings = computed(() => canAccessModule('settings'));

const isSettingsRoute = computed(() => route.path.startsWith('/settings'));

function closeSidebar() {
  uiStore.closeSidebar();
}

function isActive(href: string) {
  if (href === '/') {
    return route.path === '/';
  }
  return route.path.startsWith(href);
}
</script>

<template>
  <div>
    <!-- Mobile sidebar -->
    <TransitionRoot
      as="template"
      :show="uiStore.sidebarOpen"
    >
      <Dialog
        as="div"
        class="relative z-50 lg:hidden"
        @close="closeSidebar"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    class="-m-2.5 p-2.5"
                    @click="closeSidebar"
                  >
                    <span class="sr-only">Close sidebar</span>
                    <X
                      class="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </TransitionChild>

              <!-- Mobile sidebar content -->
              <div class="flex grow flex-col bg-white px-6 pb-2 overflow-hidden">
                <div class="flex h-16 shrink-0 items-center">
                  <h1 class="text-xl font-bold text-gray-900">
                    TMS Admin
                  </h1>
                </div>
                <nav class="flex flex-1 flex-col min-h-0">
                  <!-- Scrollable main items -->
                  <ul
                    role="list"
                    class="flex-1 min-h-0 overflow-y-auto -mx-2 space-y-1"
                  >
                    <li
                      v-for="item in navigation"
                      :key="item.name"
                    >
                      <RouterLink
                        :to="item.href"
                        :class="[
                          isActive(item.href)
                            ? 'bg-profile-50 text-black font-semibold'
                            : 'text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600',
                          'group flex gap-x-3 rounded-md p-2 text-sm leading-6',
                        ]"
                        @click="closeSidebar"
                      >
                        <Icon
                          :name="item.icon"
                          size-class="h-6 w-6 shrink-0"
                          color="text-gray-600"
                          :variant="item.iconVariant ?? 'fill'"
                        />
                        {{ item.name }}
                      </RouterLink>
                    </li>
                  </ul>
                  <!-- Pinned bottom: Settings -->
                  <ul
                    role="list"
                    class="shrink-0 -mx-2 space-y-1 border-t border-gray-100 pt-2 mt-2"
                  >
                    <li v-if="showSettings">
                      <RouterLink
                        to="/settings"
                        :class="[
                          isSettingsRoute
                            ? 'bg-profile-50 text-black font-semibold'
                            : 'text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600',
                          'group flex gap-x-3 rounded-md p-2 text-sm leading-6',
                        ]"
                        @click="closeSidebar"
                      >
                        <Icon
                          name="Settings"
                          size-class="h-6 w-6 shrink-0"
                          color="text-gray-600"
                        />
                        Settings
                      </RouterLink>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Desktop sidebar -->
    <div
      :class="[
        'hidden lg:fixed lg:top-16 lg:z-40 lg:flex lg:flex-col lg:mt-4 transition-all duration-300'
      ]"
      :style="{
        height: 'calc(100vh - 4rem - 1rem - 50px)',
        width: uiStore.sidebarCollapsed ? '60px' : '14rem'
      }"
    >
      <!-- :style="{ height: 'calc(100vh - 4rem - 1rem - 80px)' }" -->
      <div
        class="custom-sidebar flex grow flex-col border-r border-gray-200 bg-white pb-4 pt-6 overflow-hidden!"
        :class="uiStore.sidebarCollapsed ? 'px-3' : 'px-6'"
      >
        <nav class="flex flex-1 flex-col min-h-0">
          <!-- Scrollable main items -->
          <ul
            role="list"
            class="flex-1 min-h-0 overflow-y-auto"
            :class="uiStore.sidebarCollapsed ? 'space-y-1' : '-mx-2 space-y-1'"
          >
            <li
              v-for="item in navigation"
              :key="item.name"
            >
              <RouterLink
                :to="item.href"
                :class="[
                  isActive(item.href)
                    ? 'bg-profile-50 text-black font-semibold'
                    : 'text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600',
                  'group flex gap-x-3 rounded-md p-2 text-sm leading-6',
                  uiStore.sidebarCollapsed ? 'justify-center' : ''
                ]"
                :title="uiStore.sidebarCollapsed ? item.name : ''"
              >
                <Icon
                  :name="item.icon"
                  size-class="h-6 w-6 shrink-0"
                  color="text-gray-600"
                  :variant="item.iconVariant ?? 'fill'"
                />
                <span v-if="!uiStore.sidebarCollapsed">{{ item.name }}</span>
              </RouterLink>
            </li>
          </ul>
          <!-- Pinned bottom: Settings -->
          <ul
            role="list"
            class="shrink-0 border-t border-gray-100 pt-2 mt-2"
            :class="uiStore.sidebarCollapsed ? 'space-y-1' : '-mx-2 space-y-1'"
          >
            <li v-if="showSettings">
              <RouterLink
                to="/settings"
                :class="[
                  isSettingsRoute
                    ? 'bg-profile-50 text-black font-semibold'
                    : 'text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600',
                  'group flex gap-x-3 rounded-md p-2 text-sm leading-6',
                  uiStore.sidebarCollapsed ? 'justify-center' : '',
                ]"
                :title="uiStore.sidebarCollapsed ? 'Settings' : ''"
              >
                <Icon
                  name="Settings"
                  size-class="h-6 w-6 shrink-0"
                  color="text-gray-600"
                />
                <span v-if="!uiStore.sidebarCollapsed">Settings</span>
              </RouterLink>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-sidebar {
  border-radius: 0 8px 8px 0;
  background: white;
  box-shadow: 4px 0 4px 2px rgba(173, 143, 191, 0.10);
  overflow-x: hidden;
  scrollbar-gutter: stable;
  will-change: width;
}

/* Smoother scrollbar */
.custom-sidebar::-webkit-scrollbar {
  width: 6px;
}

.custom-sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-sidebar::-webkit-scrollbar-thumb {
  background: var(--color-secondary-200);
  border-radius: 3px;
}

.custom-sidebar::-webkit-scrollbar-thumb:hover {
  background: var(--color-secondary-300);
}
</style>
